import * as React from 'react';
import { AddressAutocomplete as KitAddressAutocomplete, type MapySuggestItem } from '@redhand/ui';
import { t } from '@reos/i18n';
import { suggestPropertyAddress } from '../lib/address-suggest.js';
import type { DealAddress } from '../lib/wizard-model.js';
import { WIZARD_TEXTS } from '../lib/texts.js';

/**
 * Nasepta adres nemovitosti. Od kroku C2 je samotna komponenta v kitu
 * (`@redhand/ui`), aby ji mohl pouzit i verejny web — tady zustava jen
 * dosazeni textu z i18n a vyber zdroje napovedy.
 *
 * Krok NOC3 bod 5 (nalez N17): zdrojem je NAS REGISTR ADRES (RUIAN,
 * `GET /v1/public/address-lookup`), Mapy.com jen jako zaloha — duvody a
 * pravidlo „priblizna shoda se nepredvyplni jako adresa" jsou
 * u `lib/address-suggest.ts`.
 */
export function AddressAutocomplete({
  value,
  onChange,
  inputId,
  testIdPrefix = 'wizard-address',
}: {
  readonly value: DealAddress;
  readonly onChange: (address: DealAddress) => void;
  readonly inputId?: string | undefined;
  /** Vychozi `wizard-address-*` drzi historicke testid pruvodce. */
  readonly testIdPrefix?: string | undefined;
}): React.JSX.Element {
  const suggest = React.useCallback(
    (query: string, signal: AbortSignal): Promise<readonly MapySuggestItem[]> =>
      suggestPropertyAddress(query, signal),
    [],
  );
  return (
    <KitAddressAutocomplete
      value={value}
      onChange={onChange}
      suggest={suggest}
      placeholder={WIZARD_TEXTS.addressPlaceholder}
      label={WIZARD_TEXTS.addressLabel}
      testIdPrefix={testIdPrefix}
      {...(inputId !== undefined ? { inputId } : {})}
      /* Registr napovida od tri znaku — kratsi dotaz vraci prazdno. */
      minLength={3}
      loadingLabel={t('webAdmin.wizard.addressLoading', { locale: 'cs' })}
      emptyLabel={t('webAdmin.wizard.addressNotFound', { locale: 'cs' })}
      approximateHint={t('webAdmin.wizard.addressApproximate', { locale: 'cs' })}
      mapyLogoSrc="/mapy-logo.svg"
    />
  );
}
